import { useCallback } from "react";
import { useSelector } from "react-redux";

/**
 * Lets the global file-preview sidebar open OVER a MUI `Dialog` and stay
 * usable while the dialog is still up. Three things fight it otherwise:
 *
 *   1. STACKING. The theme lifts the whole overlay scale so a dropdown opened
 *      inside a sidebar floats above it (`zIndex.modal = 13200`, see
 *      `generateMuiThemeDefaults`) — which also puts every Dialog above the
 *      preview sidebar's 13000. While a preview is open the dialog therefore
 *      drops to `DIALOG_UNDER_PREVIEW`, just below the sidebar, so the preview
 *      (and its scrim-free panel) paints on top. The dialog's own popovers
 *      still use the 13200 modal layer, so an autocomplete opened inside it
 *      keeps working.
 *   2. FOCUS. The Dialog's focus trap pulls focus back the moment you click
 *      into the preview (its viewer, toolbar, comments).
 *   3. ESCAPE. It reaches both — the preview closes AND the dialog closes.
 *
 * So while `state.filePreview.data` is set the dialog relaxes its focus trap
 * and ignores `escapeKeyDown`; the first Escape closes the preview, the next
 * one closes the dialog. Spread `dialogProps` onto the `<Dialog>`.
 *
 *   const { dialogProps } = usePreviewOverDialog(onClose);
 *   <Dialog open={open} {...dialogProps} …>
 */

/** Just under `ResizeableSidebar`'s 13000 — see (1) above. */
const DIALOG_UNDER_PREVIEW = 12990;

export const usePreviewOverDialog = (onClose) => {
  const previewOpen = useSelector((s) => !!s.filePreview?.data);

  const handleClose = useCallback(
    (event, reason) => {
      if (previewOpen && reason === "escapeKeyDown") return;
      onClose?.(event, reason);
    },
    [previewOpen, onClose],
  );

  return {
    previewOpen,
    dialogProps: {
      onClose: handleClose,
      disableEnforceFocus: previewOpen,
      sx: previewOpen ? { zIndex: DIALOG_UNDER_PREVIEW } : undefined,
    },
  };
};

export default usePreviewOverDialog;
